import {
  Component,
  computed,
  ElementRef,
  effect,
  inject,
  signal,
  Signal,
  WritableSignal,
  OnInit,
  OnDestroy,
  InputSignal,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
  FormsModule,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { ErrorMessageComponent } from '../../../layout/error-message/error-message.component';
import {
  FormInputSelectComponent,
  IObjectOption,
  SelectOption,
} from '../form-input-select.component';
import { ToSelectOptionAttributePipe } from '../../../../pipes/to-select-option-attribute/to-select-option-attribute.pipe';
import { AppendTestIdPipe } from '../../../../pipes/append-test-id/append-test-id.pipe';
import { fromEvent } from 'rxjs';
import { PopupPosition } from '../../../../directives/popup-position';
import { ToFormControlPipe } from '../../../../pipes/to-form-control/to-form-control.pipe';
import { SelectionOptionValueToNamePipe } from '../../../../pipes/selection-option-value-to-name/selection-option-value-to-name.pipe';
import { FilterTextsPipe } from '../../../../pipes/filterTexts/filterTexts.pipe';
import { LanguagePipe } from '../../../../pipes/language/language.pipe';
import { EEntityType, ERelationType } from '@web/dtos';

@Component({
  selector: 'aw-drop-down',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    ToSelectOptionAttributePipe,
    AppendTestIdPipe,
    PopupPosition,
    ToFormControlPipe,
    SelectionOptionValueToNamePipe,
    FilterTextsPipe,
    LanguagePipe,
  ],
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
  templateUrl: './drop-down.component.html',
})
export class DropDownComponent
  extends FormInputSelectComponent
  implements OnInit, OnDestroy
{
  public readonly showResetIcon: InputSignal<boolean> = input(false);
  public readonly appearance: InputSignal<'default' | 'compact'> = input<
    'default' | 'compact'
  >('default');

  public readonly selectedItem: WritableSignal<number | undefined> =
    signal(undefined);
  public readonly showList: WritableSignal<boolean> = signal(false);
  public readonly searchable: InputSignal<boolean> = input(false);
  public readonly searchPlaceholder: InputSignal<string> = input('Suchen');
  public readonly showClearOption: InputSignal<boolean> = input(false);
  public readonly clearOptionLabel: InputSignal<string> = input(
    '- Nichts auswählen -'
  );

  private readonly searchTerm: WritableSignal<string> = signal('');
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly validOptionValidator: ValidatorFn = this.isValidOption();

  public readonly filteredOptions: Signal<SelectOption[]> = computed(() => {
    const searchTerm: string = String(this.searchTerm() ?? '')
      .trim()
      .toLowerCase();
    if (!this.searchable() || !searchTerm) {
      return this.options();
    }

    return this.options().filter((option: SelectOption) =>
      this.optionLabel(option).toLowerCase().includes(searchTerm)
    );
  });

  public readonly selectedOptionIcon: Signal<string | undefined> = computed(
    () => {
      const val = this.ngControl.value;
      const option = this.options().find((opt: any) =>
        this.valueAttribute()
          ? opt[this.valueAttribute()!] === val
          : opt === val
      ) as any;
      return option?.icon;
    }
  );

  protected readonly EEntityType: typeof EEntityType = EEntityType;
  protected readonly ERelationType: typeof ERelationType = ERelationType;

  constructor(public override ngControl: NgControl) {
    super(ngControl);

    effect((): void => {
      this.options();
      this.isLoading();

      const control: AbstractControl | null = this.ngControl.control;
      if (control) {
        control.updateValueAndValidity({ emitEvent: false });
        this.setIndexFromValue(control.value);
      }
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.ngControl.control!.addValidators(this.validOptionValidator);
    this.ngControl.control!.updateValueAndValidity({ emitEvent: false });

    this.setIndexFromValue(this.ngControl.control!.value);
    this.subscription.add(
      this.searchControl.valueChanges.subscribe((value: string) => {
        this.searchTerm.set(value);
        if (this.searchable()) {
          this.selectedItem.set(undefined);
        } else {
          this.setIndexFromValue(value);
        }
      })
    );
    this.subscription.add(
      fromEvent<KeyboardEvent>(document, 'keyup').subscribe(event => {
        const options: SelectOption[] = this.filteredOptions();
        const target: EventTarget | null = event.target;
        const isTrigger: boolean =
          target instanceof HTMLElement && target.id === this.id();
        const isInsideDropdown: boolean =
          target instanceof Node &&
          this.elementRef.nativeElement.contains(target);

        if (isTrigger) {
          event.preventDefault();
          if (event.key === 'ArrowDown') {
            this.selectedItem.set(
              this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! + 1,
                    options.length
                  )
                : this.firstSelectableIndex()
            );
          } else if (event.key === 'ArrowUp') {
            this.selectedItem.set(
              this.selectedItem() !== this.firstSelectableIndex() &&
              this.selectedItem() !== undefined
                ? this.clampSelectedItem(
                    this.selectedItem()! - 1,
                    options.length
                  )
                : undefined
            );
          } else if (
            event.key === 'Enter' &&
            this.selectedItem() !== undefined &&
            this.showList()
          ) {
            if (this.showClearOption() && this.selectedItem() === -1) {
              this.clearSelection();
            } else {
              this.selectOptionAndSetItem(
                options[this.selectedItem()!],
                this.selectedItem()!
              );
            }
          } else if (event.key === ' ') {
            this.toggleList();
          }
        } else if (!isInsideDropdown && this.showList()) {
          event.preventDefault();
          this.showList.set(false);
        }
      })
    );
    this.subscription.add(
      fromEvent<MouseEvent>(document, 'mouseup').subscribe(event => {
        const target: EventTarget | null = event.target;
        const isInsideDropdown: boolean =
          target instanceof Node &&
          this.elementRef.nativeElement.contains(target);
        if (!isInsideDropdown) {
          this.showList.set(false);
        }
      })
    );
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.ngControl.control!.removeValidators(this.validOptionValidator);
  }

  public selectOptionAndSetItem(option: SelectOption, index: number): void {
    super.selectOption(option);
    this.selectedItem.set(index);
    this.showList.set(false);
  }

  public clearSelection(): void {
    if (this.ngControl.control!.disabled) {
      return;
    }

    this.searchControl.setValue('');
    this.ngControl.control!.setValue('');
    this.ngControl.control!.markAsDirty();
    this.ngControl.control!.markAsTouched();
    this.selectedItem.set(-1);
    this.showList.set(false);
  }

  public toggleList(): void {
    if (!this.showList() && this.searchable()) {
      this.searchControl.setValue('');
      this.setIndexFromValue(this.ngControl.control!.value);
    }
    this.showList.update((showList: boolean) => !showList);
  }

  private clampSelectedItem(newValue: number, maxLength: number): number {
    if (newValue >= maxLength) {
      return maxLength - 1;
    } else if (newValue < this.firstSelectableIndex()) {
      return this.firstSelectableIndex();
    }
    return newValue;
  }

  private setIndexFromValue(value: unknown): void {
    const index: number | undefined = this.findIndexFromValue(value);
    this.selectedItem.set(
      index === -1 && !this.showClearOption() ? undefined : index
    );
  }

  private firstSelectableIndex(): number {
    return this.showClearOption() ? -1 : 0;
  }

  private optionLabel(option: SelectOption): string {
    const label: unknown =
      this.name() && option && typeof option === 'object'
        ? (option as IObjectOption)[this.name()!]
        : option;
    return label?.toString() ?? '';
  }

  private isValidOption(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (
        this.isLoading() ||
        control.value === undefined ||
        control.value === null ||
        control.value === ''
      ) {
        return null;
      }
      const isInOptions = this.options().filter(option =>
        this.valueAttribute()
          ? control.value == (option as IObjectOption)[this.valueAttribute()!]
          : JSON.stringify(control.value) == JSON.stringify(option)
      );
      return isInOptions.length === 0 ? { invalidOption: true } : null;
    };
  }
}
